import { RootState } from '../../store'
import { createSelector } from '@reduxjs/toolkit'
import { Event } from './types'

export const selectEvents = (state: RootState) => state.manageEvents.events
export const selectSelectedEvent = (state: RootState) => state.manageEvents.selectedEvent
export const selectEventsLoading = (state: RootState) => state.manageEvents.loading
export const selectUpdateLoading = (state: RootState) => state.manageEvents.updateLoading
export const selectSelectedEventLoading = (state: RootState) => state.manageEvents.selectedEventLoading
export const selectEventsError = (state: RootState) => state.manageEvents.error
export const selectSelectedEventError = (state: RootState) => state.manageEvents.selectedEventError
export const selectFilters = (state: RootState) => state.manageEvents.filters
export const selectSearchQuery = (state: RootState) => state.manageEvents.searchQuery
export const selectLoadingUpdateInvitedTeamMember = (state: RootState) =>
  state.manageEvents.loadingUpdateInvitedTeamMember
export const selectErrorUpdateInvitedTeamMember = (state: RootState) =>
  state.manageEvents.errorUpdateInvitedTeamMember

// Sorting function
const sortByUpcomingStartDate = (events: Event[]) => {
  const now = new Date()
  return [...events].sort((a, b) => {
    // Handle null dates
    if (a.startDate === null && b.startDate === null) return 0
    if (a.startDate === null) return 1
    if (b.startDate === null) return -1

    const dateA = new Date(a.startDate)
    const dateB = new Date(b.startDate)

    // Get the start of today
    const today = new Date(now)
    today.setHours(0, 0, 0, 0)

    // Check if dates are today, future, or past
    const isDateAToday = dateA.toDateString() === today.toDateString()
    const isDateBToday = dateB.toDateString() === today.toDateString()
    const isDateAFuture = dateA > today
    const isDateBFuture = dateB > today

    // If both dates are today, sort by time
    if (isDateAToday && isDateBToday) {
      return dateA.getTime() - dateB.getTime()
    }

    // If one date is today, prioritize it
    if (isDateAToday) return -1
    if (isDateBToday) return 1

    // If both dates are future dates, sort by nearest date
    if (isDateAFuture && isDateBFuture) {
      return dateA.getTime() - dateB.getTime()
    }

    // If one date is future and other is past, prioritize future
    if (isDateAFuture) return -1
    if (isDateBFuture) return 1

    // If both dates are in the past, sort by most recent first
    return dateB.getTime() - dateA.getTime()
  })
}

export const selectFilteredEvents = createSelector(
  [selectEvents, selectFilters, selectSearchQuery],
  (events, filters, searchQuery) => {
    // First filter the events
    const filteredEvents = events.filter((event) => {
      // Apply filters
      const matchesFilters = Object.entries(filters).every(([key, value]) => {
        if (!value || value.length === 0) return true // Skip empty filters

        if (key === 'status') {
          if (value.length === 0 || value.length === 3) {
            // If no status is selected or all statuses are selected, show all events
            return true
          } else if (value.includes('completed')) {
            if (value.includes('active')) {
              // Show completed events OR active events
              return (
                event.isCompleted === true || (event.isCompleted === false && event.status === 'active')
              )
            } else if (value.includes('inactive')) {
              // Show completed events OR inactive events
              return (
                event.isCompleted === true ||
                (event.isCompleted === false && event.status === 'inactive')
              )
            }
            // Only completed selected
            return event.isCompleted === true
          } else if (value.includes('active') || value.includes('inactive')) {
            // Only active or inactive selected
            return event.isCompleted === false && value.includes(event.status)
          }
        }

        const eventValue = event[key as keyof typeof event]
        // Handle array-based filters for category, status, and importance
        if (Array.isArray(value)) {
          return value.includes(eventValue as string)
        }
        return eventValue === value
      })

      // Apply search query
      const matchesSearch = searchQuery
        ? event.title.toLowerCase().includes(searchQuery.toLowerCase())
        : true

      return matchesFilters && matchesSearch
    })

    // Then sort the filtered events
    return sortByUpcomingStartDate(filteredEvents)
  },
)

export const selectFilteredEventsIsNotArchived = createSelector(
  [selectFilteredEvents],
  (filteredEvents) => filteredEvents.filter((event) => !event.isArchived),
)

export const selectUpcomingEventCount = createSelector([selectEvents], (events) => {
  return events.filter(
    (event) => event.isArchived === false && event.isCompleted === false,
  ).length
})
